import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { ColDef, GridOptions } from 'ag-grid-community';
import { map, publishReplay, refCount, switchMap } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';

import { EmailActionsMenuAgRendererComponent } from './email-actions-menu-ag-renderer.component';
import { CovanceGridColumn } from '../../covance-ag-grid';
import { Email, EmailPreferenceCategory, EmailPreferenceReadModel } from './email';
import { GridColumns } from '../../shared/grid-columns';
import { EmailPreferenceLevelType } from './levels/email-preference-level-type';
import { EmailNotificationFrequencyOption } from './email-notification-frequency-option';
import { EMAIL_PREFERENCE_STORAGE_TOKEN, EmailPreferencesStorage } from './email-preferences-storage';
import { EmailNotificationHeaderAgComponent, EmailNotificationHeaderParams } from './email-notification-header-ag.component';
import { User } from '../user';
import { ActivatedRoute } from '@angular/router';
import { IMdlCustomDialogConfiguration, MdlDialogService } from '@angular-mdl/core';
import { findUser } from '../../shared/route-helpers';
import { EmailAddressFormDialog, USER_PK } from './email-address-form-dialog.component';
import { uniqBy } from 'lodash';
import { EmailPreferencesCategoriesStorage } from './email-preferences-categories-storage';

@Component({
    selector: 'email-preferences',
    template: require('./email-preferences.component.html'),
    styles: [require('./email-preferences.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{provide: EMAIL_PREFERENCE_STORAGE_TOKEN, useExisting: EmailPreferencesStorage}]
})
export class EmailPreferencesComponent {
    public readonly protocolLevel: EmailPreferenceLevelType = EmailPreferenceLevelType.Protocol;
    public readonly siteLevel: EmailPreferenceLevelType = EmailPreferenceLevelType.Site;
    public readonly userLevel: EmailPreferenceLevelType = EmailPreferenceLevelType.User;
    public readonly levelSubject$ = new BehaviorSubject<EmailPreferenceLevelType>(this.userLevel);

    public readonly gridOptions: GridOptions;
    public readonly columnDefs$: Observable<CovanceGridColumn[]>;
    public readonly rowData$: Observable<EmailPreferenceReadModel[]>;
    public readonly user: User;

    public get levels(): EmailPreferenceLevelType[] {
        if (this.user.isInvestigator) {
            return [this.userLevel, this.siteLevel];
        }
        if (this.user.isSponsor) {
            return [this.userLevel, this.protocolLevel, this.siteLevel];
        }

        throw User.notSupportedRoleError();
    }

    private get userId() {
        return this.user.metadata.userId;
    }

    constructor(
        @Inject(EMAIL_PREFERENCE_STORAGE_TOKEN) private readonly emailPreferencesStorage: EmailPreferencesStorage,
        private readonly gridColumns: GridColumns,
        route: ActivatedRoute,
        private readonly dialogService: MdlDialogService,
        emailPreferencesCategoriesStorage: EmailPreferencesCategoriesStorage
    ) {
        this.gridOptions = {
            enableFilter: false,
            enableColResize: true,
            enableSorting: false,
            rowSelection: 'multiple',
            rowDeselection: true,
            groupRowRendererParams: {
                suppressCount: true,
                checkbox: true
            },
            groupSelectsChildren: true,
            groupDefaultExpanded: 0,
        };

        this.columnDefs$ = this.levelSubject$.pipe(
            switchMap((level) => emailPreferencesCategoriesStorage.loadEmailPreferenceCategories(this.user).pipe(map(x => this.getGridColumns(x, level))))
        );

        this.rowData$ = this.levelSubject$.pipe(
            switchMap(x => this.getRowData(x)),
            publishReplay(1),
            refCount());

        this.user = findUser(route.snapshot);
    }

    public onLevelChange(level: EmailPreferenceLevelType) {
        this.levelSubject$.next(level);
    }

    public async showEmailAddressForm() {
        const configDialog: IMdlCustomDialogConfiguration = {
            component: EmailAddressFormDialog,
            providers: [
                { provide: USER_PK, useValue: this.userId },
                { provide: EMAIL_PREFERENCE_STORAGE_TOKEN, useValue: this.emailPreferencesStorage},
            ],
            isModal: true,
            clickOutsideToClose: true
        };

        this.dialogService.showCustomDialog(configDialog);
    }

    public async deleteEmails() {
        let selectedEmails = this.gridOptions.api.getSelectedNodes().map(row => row.data.email) as Email[];

        if (!selectedEmails.length) {
            await this.dialogService.alert(`You should select at least one email`, 'OK');
            return;
        }

        if (selectedEmails.some(email => email.isDefault)) {
            await this.dialogService.alert(`You are not able to delete default email`, 'OK');
            return;
        }

        selectedEmails = uniqBy(selectedEmails, email => email.emailAddress);

        try {
            await this.dialogService
                .confirm(`Are you sure you want to delete ${selectedEmails.map(item => item.emailAddress).join(', ')} email${selectedEmails.length > 1 ? 's' : ''}?`, 'Cancel', 'Yes')
                .toPromise();

            await this.emailPreferencesStorage.deleteEmails(selectedEmails, this.userId);
        } catch (e) { }
    }

    private getRowData(level: EmailPreferenceLevelType): Observable<EmailPreferenceReadModel[]> {
        this.emailPreferencesStorage.reloadEmailPreferences({user: this.user, levelIndicator: level});
        return this.emailPreferencesStorage.emailPreferences;
    }

    private getGridColumns(emailPreferenceCategories: EmailPreferenceCategory[], level: EmailPreferenceLevelType): ColDef[] {
        let startColumns: ColDef[];
        if (this.user.isSponsor) {
            startColumns = this.getSponsorStartColumns(level);
        } else if (this.user.isInvestigator) {
            startColumns = this.getInvestigatorStartColumns(level);
        } else {
            throw User.notSupportedRoleError();
        }

        const dynamicColumns: ColDef[] = emailPreferenceCategories.map(category => {
            const options: EmailNotificationFrequencyOption[] = [EmailNotificationFrequencyOption.None, EmailNotificationFrequencyOption.Daily];
            if (this.user.isInvestigator || category.categoryId === 'result-alerts') {
                options.push(EmailNotificationFrequencyOption.Immediate);
            }

            return {
                headerName: category.categoryName,
                minWidth: 100,
                headerComponentFramework: EmailNotificationHeaderAgComponent,
                headerComponentParams: {
                    userId: this.userId,
                    categoryName: category.categoryName,
                    categoryId: category.categoryId,
                    options: options,
                    currentLevel: level,
                } as EmailNotificationHeaderParams,
                valueGetter: (params: { data: EmailPreferenceReadModel }) => {
                    if (!params.data) {
                        return null;
                    }

                    const preference = params.data.emailPreferences.find(pref => pref.categoryId === category.categoryId);
                    if (!preference) {
                        throw Error('unable to get preference for provided category');
                    }
                    return EmailNotificationFrequencyOption[preference.prefValue];
                },
            };
        });

        const endColumns: ColDef[] = [
            this.gridColumns.actionsMenuColumn(
                {
                    cellRendererFramework: EmailActionsMenuAgRendererComponent,
                    userId: this.userId,
                })
        ];

        return [
            ...startColumns,
            ...dynamicColumns,
            ...endColumns
        ];
    }

    private getInvestigatorStartColumns(level: EmailPreferenceLevelType): ColDef[] {
        const rowGroupIndex = level === this.siteLevel ? 0 : null;
        const hide = level === this.siteLevel;

        const startColumns: ColDef[] = [
            {
                headerName: 'Select',
                checkboxSelection: true,
                cellClass: 'selection-checkbox',
                width: 50,
            },
            {
                headerName: 'Email',
                field: 'email',
                width: 240,
                rowGroupIndex: rowGroupIndex,
                hide: hide,
            },
        ];

        if (level === this.siteLevel) {
            startColumns.push(
                {
                    headerName: 'Site',
                    field: 'site',
                    width: 150,
                });
        }

        return startColumns;
    }

    private getSponsorStartColumns(level: EmailPreferenceLevelType): ColDef[] {
        const rowGroupIndex = level === this.protocolLevel || level === this.siteLevel ? 0 : null;
        const hide = level === this.protocolLevel || level === this.siteLevel;

        const startColumns: ColDef[] = [
            {
                headerName: 'Select',
                checkboxSelection: true,
                cellClass: 'selection-checkbox',
                width: 50,
            },
            {
                headerName: 'Email',
                field: 'email',
                width: 240,
                rowGroupIndex: rowGroupIndex,
                hide: hide,
            },
        ];

        if (level === this.protocolLevel) {
            startColumns.push(
                {
                    headerName: 'Protocol',
                    field: 'protocol',
                    width: 150,
                });
        }

        if (level === this.siteLevel) {
            startColumns.push(
                {
                    headerName: 'Protocol',
                    field: 'protocol',
                    width: 150,
                    rowGroupIndex: 1,
                    hide: true,
                });

            startColumns.push(
                {
                    headerName: 'Site',
                    field: 'site',
                    width: 150,
                });
        }

        return startColumns;
    }
}
